import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  Button
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { formatMoani } from '../utils/moaniUtils';

function IPGroupRow({ group, onUserClick }) {
  const [open, setOpen] = useState(false);

  const getSeverityColor = (count) => {
    if (count >= 10) return 'error';
    if (count >= 5) return 'warning';
    return 'info';
  };

  const getRiskLevel = (stats) => {
    const { bannedCount, cheaterCount, userCount } = group;
    const bannedRatio = bannedCount / userCount;
    const cheaterRatio = cheaterCount / userCount;
    
    if (bannedRatio > 0.5 || cheaterRatio > 0.5) return { level: 'High', color: 'error' };
    if (bannedRatio > 0.2 || cheaterRatio > 0.2) return { level: 'Medium', color: 'warning' };
    return { level: 'Low', color: 'success' };
  };

  const risk = getRiskLevel(group.stats);

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
          cursor: 'pointer'
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton size="small">
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontFamily="monospace">
            {group.ip}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip 
            label={group.userCount} 
            color={getSeverityColor(group.userCount)} 
            size="small"
          />
        </TableCell>
        <TableCell align="center">
          <Chip 
            label={risk.level} 
            color={risk.color} 
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell align="center">
          {group.stats.bannedCount > 0 ? (
            <Chip 
              icon={<BlockIcon />}
              label={group.stats.bannedCount} 
              color="error" 
              size="small"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">0</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {group.stats.cheaterCount > 0 ? (
            <Chip 
              icon={<WarningIcon />}
              label={group.stats.cheaterCount} 
              color="warning" 
              size="small"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">0</Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontFamily="monospace">
            {formatMoani(group.stats.totalMoani)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" color="text.secondary">
            {group.stats.registrationIpCount} / {group.userCount}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Users on IP {group.ip}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Wallet</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">MOANI</TableCell>
                    <TableCell>Reg IP Match</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.users.map((user) => (
                    <TableRow 
                      key={user.id}
                      sx={{ 
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.03)' },
                        cursor: 'pointer',
                        opacity: user.banned ? 0.6 : 1
                      }}
                      onClick={() => onUserClick && onUserClick(user)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {user.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize="0.75rem">
                          {user.email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {user.wallet ? `${user.wallet.substring(0, 6)}...${user.wallet.substring(38)}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {user.banned && (
                            <Tooltip title="Banned">
                              <BlockIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                          {user.cheater && (
                            <Tooltip title="Flagged as Cheater">
                              <WarningIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                          {!user.banned && !user.cheater && (
                            <Tooltip title="Normal">
                              <CheckCircleIcon color="success" fontSize="small" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {formatMoani(user.moani)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {user.hasThisAsRegistrationIp ? (
                          <Chip label="Yes" color="primary" size="small" />
                        ) : (
                          <Chip label="History Only" variant="outlined" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize="0.75rem">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function IPGroupedUsers() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skipPlayerNames, setSkipPlayerNames] = useState(true);
  const [stats, setStats] = useState({ totalGroups: 0, totalUsers: 0 });

  const fetchIPGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/ip-analysis/grouped?skipPlayerNames=${skipPlayerNames}&minUsersPerIp=2`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch IP groups');
      }
      const data = await response.json();
      setGroups(data.groups || []);
      setStats({
        totalGroups: data.totalGroups || 0,
        totalUsers: data.totalUsers || 0
      });
    } catch (err) {
      console.error('Error fetching IP groups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIPGroups();
  }, [skipPlayerNames]);

  const handleUserClick = (user) => {
    // Navigate to user details - you can implement this based on your routing
    window.open(`/users/${user.id}`, '_blank');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            IP-Based Multi-Account Detection
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Users grouped by shared IP addresses
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={skipPlayerNames}
                onChange={(e) => setSkipPlayerNames(e.target.checked)}
                color="primary"
              />
            }
            label="Skip 'Player' users"
          />
          <Button variant="outlined" onClick={fetchIPGroups} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {stats.totalGroups > 0 && (
        <Stack direction="row" spacing={2} mb={2}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              IP Groups
            </Typography>
            <Typography variant="h5">
              {stats.totalGroups}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total Users in Groups
            </Typography>
            <Typography variant="h5">
              {stats.totalUsers}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Avg Users per IP
            </Typography>
            <Typography variant="h5">
              {(stats.totalUsers / stats.totalGroups).toFixed(1)}
            </Typography>
          </Paper>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px" />
                <TableCell>IP Address</TableCell>
                <TableCell align="center">User Count</TableCell>
                <TableCell align="center">Risk Level</TableCell>
                <TableCell align="center">Banned</TableCell>
                <TableCell align="center">Cheaters</TableCell>
                <TableCell align="right">Total MOANI</TableCell>
                <TableCell align="center">Reg IP Matches</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No IP groups found with multiple users
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <IPGroupRow 
                    key={group.ip} 
                    group={group}
                    onUserClick={handleUserClick}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

